import { useState, useMemo, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import PropTypes from 'prop-types';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import {
  InputAdornment,
  TextField,
  Box,
  FormControl,
  Select,
  Button,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  IconButton,
  Divider,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ClearIcon from '@mui/icons-material/Clear';
import * as XLSX from 'xlsx';
import { getAllPurchaseOrders } from './functions/get_all_purchase_orders.function';
import { notify } from '../../common/functions/react-toastify.function';
import { normalizePoForCompare, parseSystemPoPdf } from './functions/parse_system_po_pdf.function';
import { saveSystemPoItemDetails } from './functions/save_system_po_item_details.function';
import { getSystemPoItemDetails } from './functions/get_system_po_item_details.function';
import { updateSystemPoAuthorizedRecord } from './functions/update_system_po_authorized_record.function';

export const SystemPurchaseOrderTable = forwardRef((props, ref) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVendor, setSelectedVendor] = useState('');
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const pdfInputRef = useRef(null);
  const [pdfTargetRow, setPdfTargetRow] = useState(null);
  const [isPoUploadDialogOpen, setIsPoUploadDialogOpen] = useState(false);
  const [selectedPdfFile, setSelectedPdfFile] = useState(null);
  const [parsedPoData, setParsedPoData] = useState(null);
  const [viewDialog, setViewDialog] = useState({
    open: false,
    po: null,
    items: [],
    loading: false,
  });
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isPoUploadProcessing, setIsPoUploadProcessing] = useState(false);
  const [detailsTargetRow, setDetailsTargetRow] = useState(null);
  const [editForm, setEditForm] = useState({
    po: '',
    po_date: '',
    vendor: '',
    total_amount: '',
    po_delivery_date: '',
  });
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const vendors = useMemo(() => {
    const uniqueVendors = [...new Set(purchaseOrders.map((po) => po.vendor).filter(Boolean))];
    return uniqueVendors.sort();
  }, [purchaseOrders]);

  const mapApiDataToTableFormat = (apiData) => {
    if (!Array.isArray(apiData)) {
      return [];
    }

    return apiData.map((item) => {
      const formatDate = (dateString) => {
        if (!dateString) {
          return null;
        }
        try {
          const d = new Date(dateString);
          if (Number.isNaN(d.getTime())) {
            return String(dateString);
          }
          return d.toISOString().slice(0, 10);
        } catch {
          return String(dateString);
        }
      };

      return {
        id: item.id,
        po: item.po || null,
        poDate: formatDate(item.po_date),
        vendor: item.vendor || null,
        totalAmount: item.total_amount || null,
        poDeliveryDate: formatDate(item.po_delivery_date),
        type: item.type || null,
        uploadedBy: item.uploaded_by || null,
        uploadedAt: item.uploaded_at || null,
      };
    });
  };

  const fetchPurchaseOrders = async () => {
    setLoading(true);
    try {
      const apiData = await getAllPurchaseOrders();
      const mappedData = mapApiDataToTableFormat(apiData);
      setPurchaseOrders(mappedData);
    } catch (err) {
      console.error('Error fetching purchase orders:', err);
      setPurchaseOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPurchaseOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removePurchaseOrder = (id) => {
    setPurchaseOrders((prev) => {
      const idToRemove = Number(id);
      return prev.filter((item) => Number(item.id) !== idToRemove);
    });
  };

  const addOrUpdatePurchaseOrders = (uploadResponseData) => {
    if (!Array.isArray(uploadResponseData) || uploadResponseData.length === 0) {
      return;
    }

    const mappedUploadData = mapApiDataToTableFormat(uploadResponseData);

    setPurchaseOrders((prev) => {
      const updated = [...prev];

      mappedUploadData.forEach((newRecord) => {
        const existingIndex = updated.findIndex((item) => item.id === newRecord.id);
        if (existingIndex >= 0) {
          updated[existingIndex] = newRecord;
        } else {
          updated.push(newRecord);
        }
      });

      return updated;
    });
  };

  const updatePurchaseOrder = (updateResponseData) => {
    if (!updateResponseData || !updateResponseData.id) {
      return;
    }

    const mappedUpdateData = mapApiDataToTableFormat([updateResponseData]);

    if (mappedUpdateData.length === 0) {
      return;
    }

    const updatedRecord = mappedUpdateData[0];

    setPurchaseOrders((prev) => {
      const updated = [...prev];
      const existingIndex = updated.findIndex(
        (item) => Number(item.id) === Number(updatedRecord.id)
      );

      if (existingIndex >= 0) {
        updated[existingIndex] = updatedRecord;
      }

      return updated;
    });
  };

  const handleDownloadExcel = (tableInstance) => {
    let dataToDownload = filteredPurchaseOrders;

    if (tableInstance) {
      const filteredRows = tableInstance.getFilteredRowModel().rows;
      dataToDownload = filteredRows.map((row) => row.original);
    }

    if (dataToDownload.length === 0) {
      return;
    }

    try {
      const excelData = dataToDownload.map((po) => {
        return {
          PO: po.po || '',
          'PO Date': po.poDate || '',
          Vendor: po.vendor || '',
          'Total Amount': po.totalAmount || '',
          'PO Delivery Date': po.poDeliveryDate || '',
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'System POs');
      const currentDate = new Date().toISOString().split('T')[0];
      const filename = `System_Purchase_Orders_${currentDate}.xlsx`;
      XLSX.writeFile(workbook, filename);
    } catch (error) {
      console.error('Error downloading Excel file:', error);
    }
  };

  const handleUploadPoClick = (row) => {
    setPdfTargetRow(row);
    setSelectedPdfFile(null);
    setParsedPoData(null);
    setIsPoUploadDialogOpen(true);
    if (pdfInputRef.current) {
      pdfInputRef.current.value = '';
      pdfInputRef.current.click();
    }
  };

  const handlePdfSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    if (
      file.type !== 'application/pdf' &&
      !String(file.name || '')
        .toLowerCase()
        .endsWith('.pdf')
    ) {
      notify('error', 'Only PDF files are allowed');
      return;
    }

    if (!pdfTargetRow?.po) {
      notify('error', 'Invalid PO row selected');
      return;
    }

    try {
      setIsPoUploadProcessing(true);
      const parsed = await parseSystemPoPdf(file);
      const pdfPoRaw = parsed?.poNo || parsed?.purchaseOrderNo || '';
      const pdfPo = normalizePoForCompare(pdfPoRaw);
      const rowPo = normalizePoForCompare(pdfTargetRow.po);

      const isMatch =
        pdfPo && rowPo && (pdfPo === rowPo || pdfPo.endsWith(rowPo) || rowPo.endsWith(pdfPo));

      if (!isMatch) {
        // On mismatch: show only error message (no parsed details).
        setSelectedPdfFile(null);
        setParsedPoData(null);
        notify(
          'error',
          `PO number mismatch. PDF Po number is  : "${pdfPoRaw || 'N/A'}" and you are uploading on Po: "${pdfTargetRow.po}"`
        );
        return;
      }

      console.log('System PO PDF parsed fields:', {
        poNo: pdfPoRaw,
        orderTo: parsed.orderTo,
        purchaseOrderDate: parsed.purchaseOrderDate,
        deliveryDate: parsed.deliveryDate,
      });
      const normalizedLineItems = (parsed.lineItems || []).map((r) => ({
        serialNo: String(r.serialNo || ''),
        itemType: String(r.itemType || ''),
        itemCode: String(r.itemCode || ''),
        description: String(r.description || ''),
        uom: String(r.uom || ''),
        quantity: String(r.quantity || ''),
        unitPrice: String(r.unitPrice || ''),
        value: String(r.value || ''),
      }));

      console.log('System PO PDF line items:', normalizedLineItems);
      console.log('System PO PDF line items (JSON):', JSON.stringify(normalizedLineItems, null, 2));
      if (parsed?.debug?.serial10) {
        console.log('System PO PDF debug serial 10 candidates:', parsed.debug.serial10);
      }

      // Save item details in DB (delete previous + insert new)
      const poId = Number(pdfTargetRow?.id);
      if (!Number.isFinite(poId)) {
        notify('error', 'Invalid PO id for saving line items');
        return;
      }

      const itemsPayload = normalizedLineItems.map((it) => ({
        item_code: it.itemCode,
        item_type: it.itemType,
        detailed_description: it.description,
        quantity: it.quantity,
        unit_price: it.unitPrice,
        um: it.uom,
      }));

      const saveRes = await saveSystemPoItemDetails({
        po_id: poId,
        items: itemsPayload,
        po_date: parsed?.purchaseOrderDate || null,
        po_delivery_date: parsed?.deliveryDate || null,
      });
      if (!saveRes.success) {
        notify('error', saveRes.error || 'Failed to save PO item details');
        return;
      }
      notify('success', saveRes.message || 'PO item details saved successfully');

      setSelectedPdfFile(file);
      setParsedPoData(parsed);
      // Optimistically update table row dates (avoid refetch).
      setPurchaseOrders((prev) =>
        (prev || []).map((r) => {
          if (Number(r?.id) !== poId) {
            return r;
          }
          return {
            ...r,
            poDate: r?.poDate || parsed?.purchaseOrderDate || r?.poDate,
            poDeliveryDate: r?.poDeliveryDate || parsed?.deliveryDate || r?.poDeliveryDate,
          };
        })
      );
      setPdfTargetRow((prev) => {
        if (!prev || Number(prev?.id) !== poId) {
          return prev;
        }
        return {
          ...prev,
          poDate: prev?.poDate || parsed?.purchaseOrderDate || prev?.poDate,
          poDeliveryDate: prev?.poDeliveryDate || parsed?.deliveryDate || prev?.poDeliveryDate,
        };
      });
    } catch (err) {
      console.error('Failed to parse PO PDF:', err);
      notify('error', 'Failed to read PDF. Please try another PDF.');
    } finally {
      setIsPoUploadProcessing(false);
      // keep row selected so user can retry upload in dialog
    }
  };

  const handleDialogClose = () => {
    setIsPoUploadDialogOpen(false);
    setSelectedPdfFile(null);
    setParsedPoData(null);
    setPdfTargetRow(null);
  };

  const handlePoUploadDialogClose = (event, reason) => {
    // While API is in progress, prevent closing on backdrop click / Escape.
    if (isPoUploadProcessing && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return;
    }
    handleDialogClose();
  };

  const openViewDetails = async (row) => {
    // if (chk) {
    //   notify('success', 'Coming Soon In Progress');
    //   return;
    // }
    const poRow = row;
    setItemSearchTerm('');
    setViewDialog({ open: true, po: poRow, items: [], loading: true });
    if (!poRow?.id) {
      setViewDialog({ open: true, po: poRow, items: [], loading: false });
      return;
    }
    try {
      const res = await getSystemPoItemDetails(poRow.id);
      if (!res.success) {
        notify('error', res.error || 'Failed to fetch PO item details');
        setViewDialog({ open: true, po: poRow, items: [], loading: false });
        return;
      }
      setViewDialog({
        open: true,
        po: poRow,
        items: Array.isArray(res.data) ? res.data : [],
        loading: false,
      });
    } finally {
      setViewDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  const openEditDetails = (row) => {
    // if (chk) {
    //   notify('success', 'Coming Soon In Progress');
    //   return;
    // }
    setDetailsTargetRow(row);
    setEditForm({
      po: row?.po || '',
      po_date: row?.poDate || '',
      vendor: row?.vendor || '',
      total_amount: row?.totalAmount || '',
      po_delivery_date: row?.poDeliveryDate || '',
    });
    setIsEditDialogOpen(true);
  };

  const closeDetailsDialogs = () => {
    setViewDialog({ open: false, po: null, items: [], loading: false });
    setItemSearchTerm('');
    setIsEditDialogOpen(false);
    setDetailsTargetRow(null);
    setEditForm({
      po: '',
      po_date: '',
      vendor: '',
      total_amount: '',
      po_delivery_date: '',
    });
  };

  const handleSaveEdit = async () => {
    if (!detailsTargetRow?.id) {
      notify('error', 'Invalid PO row');
      return;
    }
    if (!String(editForm.total_amount || '').trim()) {
      notify('error', 'Total Amount is required');
      return;
    }
    setIsSavingEdit(true);
    try {
      const res = await updateSystemPoAuthorizedRecord(detailsTargetRow.id, {
        po_date: editForm.po_date || null,
        vendor: editForm.vendor || null,
        total_amount: editForm.total_amount,
        po_delivery_date: editForm.po_delivery_date || null,
      });
      if (!res.success) {
        notify('error', res.error || 'Failed to update PO');
        return;
      }
      notify('success', res.message || 'PO updated successfully');
      await fetchPurchaseOrders();
      closeDetailsDialogs();
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleDialogPickFile = () => {
    if (pdfInputRef.current) {
      pdfInputRef.current.value = '';
      pdfInputRef.current.click();
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const dropped = e.dataTransfer.files?.[0];
    if (!dropped) {
      return;
    }
    handlePdfSelected({ target: { files: [dropped] } });
  };

  useImperativeHandle(ref, () => ({
    refetchPurchaseOrders: fetchPurchaseOrders,
    removePurchaseOrder,
    addOrUpdatePurchaseOrders,
    updatePurchaseOrder,
  }));

  const filteredPurchaseOrders = useMemo(() => {
    let filtered = purchaseOrders;

    if (selectedVendor) {
      filtered = filtered.filter((po) => po.vendor === selectedVendor);
    }

    if (searchTerm) {
      filtered = filtered.filter((po) =>
        Object.values(po).some((val) =>
          String(val).toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    return filtered;
  }, [selectedVendor, searchTerm, purchaseOrders]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'po',
        header: 'PO',
        size: 140,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          const po = cell.getValue();
          return <span style={{ fontSize: '14px', color: '#1D242E' }}>{po || 'N/A'}</span>;
        },
      },
      {
        accessorKey: 'type',
        header: 'Type',
        size: 100,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          const type = cell.getValue();
          return <span style={{ fontSize: '14px', color: '#667085' }}>{type || 'N/A'}</span>;
        },
      },
      {
        accessorKey: 'poDate',
        header: 'PO Date',
        size: 140,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          const date = cell.getValue();
          return <span style={{ fontSize: '14px', color: '#667085' }}>{date || 'N/A'}</span>;
        },
      },
      {
        accessorKey: 'vendor',
        header: 'Vendor',
        size: 200,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          const vendor = cell.getValue();
          return <span style={{ fontSize: '14px', color: '#667085' }}>{vendor || 'N/A'}</span>;
        },
      },
      {
        accessorKey: 'totalAmount',
        header: 'Total Amount',
        size: 150,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          const amt = cell.getValue();
          return <span style={{ fontSize: '14px', color: '#667085' }}>{amt || 'N/A'}</span>;
        },
      },
      {
        accessorKey: 'poDeliveryDate',
        header: 'PO Delivery Date',
        size: 150,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          const date = cell.getValue();
          return <span style={{ fontSize: '14px', color: '#667085' }}>{date || 'N/A'}</span>;
        },
      },
      {
        id: 'uploadPo',
        header: 'Upload PO',
        size: 140,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ row }) => (
          <Button
            variant="contained"
            size="small"
            onClick={() => handleUploadPoClick(row.original)}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              backgroundColor: '#2e7d32',
              color: '#fff',
              '&:hover': { backgroundColor: '#1b5e20' },
            }}
          >
            Upload PO
          </Button>
        ),
      },
      {
        id: 'details',
        header: 'Details',
        size: 110,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ row }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => {
                openViewDetails(row.original);
              }}
              sx={{ border: '1px solid #e5e7eb', borderRadius: '8px' }}
            >
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => {
                openEditDetails(row.original);
              }}
              sx={{ border: '1px solid #e5e7eb', borderRadius: '8px' }}
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: filteredPurchaseOrders,
    state: { isLoading: loading },
    muiCircularProgressProps: {
      color: 'inherit',
      size: 40,
      sx: { color: '#999999' },
    },
    enableGlobalFilter: false,
    enableGlobalFilterModes: false,
    positionGlobalFilter: 'none',
    enableColumnFilterModes: false,
    enableColumnOrdering: false,
    enableGrouping: false,
    enableRowSelection: false,
    enableSelectAll: false,
    enableColumnPinning: false,
    enableFacetedValues: false,
    enableSorting: false,
    enableDensityToggle: false,
    paginationDisplayMode: 'pages',
    positionToolbarAlertBanner: 'bottom',
    initialState: {
      pagination: {
        pageSize: 10,
      },
      columnOrder: [
        'po',
        'type',
        'poDate',
        'vendor',
        'totalAmount',
        'poDeliveryDate',
        'uploadPo',
        'details',
      ],
    },
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
    },
    muiPaginationProps: {
      color: 'secondary',
      rowsPerPageOptions: [10, 20, 30, 40],
      shape: 'rounded',
      variant: 'outlined',
      sx: {
        '& .MuiPaginationItem-root.Mui-selected': {
          backgroundColor: '#BD1D23',
          color: 'white',
        },
      },
    },
    enableBottomToolbar: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enablePagination: true,
    muiTableBodyRowProps: {
      sx: {
        height: 52,
        minHeight: 52,
        maxHeight: 52,
      },
    },
    muiTableBodyCellProps: {
      sx: {
        color: '#667085',
        backgroundColor: '#fff',
        fontSize: '12px',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: '125%',
        padding: '0 12px',
        '&:first-of-type': {
          paddingLeft: '12px !important',
        },
      },
    },
    muiTableHeadCellProps: {
      sx: {
        color: '#344054',
        backgroundColor: '#E8E9E9',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: '600',
        lineHeight: '125%',
        height: '56px',
        padding: '0 12px',
        alignItems: 'center',
        '&:first-of-type': {
          paddingLeft: '18px !important',
        },
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: 'none',
      },
    },
    muiTableHeadRowProps: {
      sx: {
        height: 56,
        '& .MuiTableCell-root': {
          padding: '6px 12px',
          verticalAlign: 'middle',
        },
      },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Search"
            size="small"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoComplete="off"
            sx={{
              backgroundColor: '#fff',
              width: '300px',
              '& .MuiInputBase-input': {
                color: 'gray',
                '::placeholder': {
                  color: '#b3b9c4',
                  opacity: 1,
                },
              },
              '& .MuiOutlinedInput-root': {
                border: '1px solid #ccc',
                borderRadius: '10px',
                paddingRight: 0,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: '#999' }} />
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => handleDownloadExcel(table)}
            disabled={filteredPurchaseOrders.length === 0}
            sx={{
              backgroundColor: 'transparent',
              border: '1px solid #ccc',
              color: '#333',
              borderRadius: '10px',
              textTransform: 'none',
              padding: '8px 16px',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                border: '1px solid #ccc',
              },
              '&:disabled': {
                backgroundColor: 'transparent',
                border: '1px solid #ccc',
                color: '#999',
                opacity: 0.6,
              },
            }}
          >
            Download
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={selectedVendor}
              onChange={(e) => setSelectedVendor(e.target.value)}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 200,
                  },
                },
              }}
              sx={{
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                color: '#757575',
                borderRadius: '10px',
                '& .MuiSelect-select': {
                  padding: '8px 12px',
                },
                '&:hover': {
                  border: '1px solid #ccc',
                },
                '&.Mui-focused': {
                  border: '1px solid #ccc',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              }}
            >
              <MenuItem
                value=""
                sx={{
                  borderBottom: '1px solid #eee',
                  fontSize: '13px',
                  color: '#656e7a',
                  lineHeight: 1.4,
                }}
              >
                VENDOR
              </MenuItem>
              {vendors.map((vendor, index) => (
                <MenuItem
                  key={vendor}
                  value={vendor}
                  sx={{
                    fontSize: '13px',
                    color: '#656e7a',
                    lineHeight: 1.4,
                    borderBottom: index !== vendors.length - 1 ? '1px solid #eee' : 'none',
                  }}
                >
                  {vendor}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
    ),
  });

  return (
    <div className="purchase-order-table-main-container">
      <input
        ref={pdfInputRef}
        type="file"
        accept="application/pdf,.pdf"
        onChange={handlePdfSelected}
        style={{ display: 'none' }}
      />

      <Dialog
        open={isPoUploadDialogOpen}
        onClose={handlePoUploadDialogClose}
        maxWidth="lg"
        fullWidth
        disableEscapeKeyDown={isPoUploadProcessing}
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Typography sx={{ fontWeight: 700 }}>Click or drag file to fetch from PO</Typography>
          <IconButton
            onClick={() => {
              if (!isPoUploadProcessing) {
                handleDialogClose();
              }
            }}
            disabled={isPoUploadProcessing}
            size="small"
            title={isPoUploadProcessing ? 'Processing…' : 'Close'}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {isPoUploadProcessing && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 10,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                backgroundColor: 'rgba(255, 255, 255, 0.92)',
                backdropFilter: 'blur(2px)',
              }}
            >
              <CircularProgress size={44} />
              <Typography sx={{ fontSize: 13, color: '#667085' }}>
                Uploading PO item details…
              </Typography>
            </Box>
          )}
          <Box
            onClick={handleDialogPickFile}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            sx={{
              border: '2px dashed #667687ff',
              borderRadius: '6px',
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              mb: 2,
              backgroundColor: selectedPdfFile ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
              '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' },
            }}
          >
            <Typography sx={{ fontSize: '14px', color: '#666' }}>
              Upload only PDF file. Row PO: <b>{pdfTargetRow?.po ?? '—'}</b>
            </Typography>
            {selectedPdfFile && (
              <Typography sx={{ fontSize: '13px', mt: 1, color: '#1976d2' }}>
                Selected: {selectedPdfFile.name}
              </Typography>
            )}
          </Box>

          {parsedPoData && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: '14px' }}>
                  <b>Purchase Order No:</b>{' '}
                  {parsedPoData.poNo || parsedPoData.purchaseOrderNo || '—'}
                </Typography>
                <Typography sx={{ fontSize: '14px' }}>
                  <b>Order To:</b> {parsedPoData.orderTo || '—'}
                </Typography>
                <Typography sx={{ fontSize: '14px' }}>
                  <b>Purchase Order Date:</b> {parsedPoData.purchaseOrderDate || '—'}
                </Typography>
                <Typography sx={{ fontSize: '14px' }}>
                  <b>Delivery Date:</b> {parsedPoData.deliveryDate || '—'}
                </Typography>
              </Box>

              <Typography sx={{ fontWeight: 600, mb: 1 }}>Line items</Typography>
              {Array.isArray(parsedPoData.lineItems) && parsedPoData.lineItems.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Item Type</TableCell>
                      <TableCell>Item Code</TableCell>
                      <TableCell>Detailed Description</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Unit Price</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parsedPoData.lineItems.map((r, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{r.itemType}</TableCell>
                        <TableCell>{r.itemCode}</TableCell>
                        <TableCell>{r.description}</TableCell>
                        <TableCell>{r.quantity}</TableCell>
                        <TableCell>{r.unitPrice}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography sx={{ fontSize: '13px', color: '#d32f2f' }}>
                  Unable to extract table data from this PO PDF. Check console logs for parsed
                  output.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} variant="outlined" disabled={isPoUploadProcessing}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog
        open={viewDialog.open}
        onClose={() => {
          setViewDialog({ open: false, po: null, items: [], loading: false });
          setItemSearchTerm('');
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 18 }}>
            PO Details & Item Details — {viewDialog.po?.po || '—'}
          </Typography>
          <IconButton
            size="small"
            onClick={() => {
              setViewDialog({ open: false, po: null, items: [], loading: false });
              setItemSearchTerm('');
            }}
            title="Close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          {(() => {
            const poDetails = viewDialog.po || {};

            return (
              <>
                <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                  PO Information
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: 1.5,
                    mb: 3,
                    p: 1.5,
                    bgcolor: '#fafafa',
                    borderRadius: 1,
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      PO Number
                    </Typography>
                    <br />
                    <Typography variant="body2">{poDetails.po ?? 'N/A'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      PO Date
                    </Typography>
                    <br />
                    <Typography variant="body2">{poDetails.poDate ?? 'N/A'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Vendor
                    </Typography>
                    <br />
                    <Typography variant="body2">{poDetails.vendor ?? 'N/A'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Total Amount
                    </Typography>
                    <br />
                    <Typography variant="body2">{poDetails.totalAmount ?? 'N/A'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      PO Delivery Date
                    </Typography>
                    <br />
                    <Typography variant="body2">{poDetails.poDeliveryDate ?? 'N/A'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Uploaded By
                    </Typography>
                    <br />
                    <Typography variant="body2">{poDetails.uploadedBy ?? 'N/A'}</Typography>
                  </Box>
                </Box>

                <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                  Item Details
                </Typography>

                {viewDialog.loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={32} />
                  </Box>
                ) : viewDialog.items.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No item details found for this PO.
                  </Typography>
                ) : (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                      <TextField
                        size="small"
                        placeholder="Search items"
                        value={itemSearchTerm}
                        onChange={(e) => setItemSearchTerm(e.target.value)}
                        autoComplete="off"
                        sx={{
                          backgroundColor: '#fff',
                          width: { xs: '100%', sm: 320 },
                          '& .MuiInputBase-input': {
                            color: 'gray',
                            '::placeholder': {
                              color: '#b3b9c4',
                              opacity: 1,
                            },
                          },
                          '& .MuiOutlinedInput-root': {
                            border: '1px solid #ccc',
                            borderRadius: '10px',
                            paddingRight: 0,
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon style={{ color: '#999' }} />
                            </InputAdornment>
                          ),
                          endAdornment: itemSearchTerm ? (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => setItemSearchTerm('')}>
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ) : null,
                        }}
                      />
                    </Box>

                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Item Code</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Item Type</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>UM</TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="right">
                              Qty
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="right">
                              Unit Price
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(viewDialog.items || [])
                            .filter((it) => {
                              const q = String(itemSearchTerm || '')
                                .toLowerCase()
                                .trim();
                              if (!q) {
                                return true;
                              }
                              const haystack = [
                                it?.item_code,
                                it?.item_type,
                                it?.detailed_description,
                                it?.um,
                                it?.quantity,
                                it?.unit_price,
                              ]
                                .filter((v) => v != null)
                                .map((v) => String(v).toLowerCase())
                                .join(' ');
                              return haystack.includes(q);
                            })
                            .map((it, idx) => (
                              <TableRow key={`${it.item_code || ''}-${idx}`}>
                                <TableCell>{it.item_code || '—'}</TableCell>
                                <TableCell>{it.item_type || '—'}</TableCell>
                                <TableCell sx={{ maxWidth: 520 }}>
                                  <Typography sx={{ fontSize: 13, color: '#1D242E' }}>
                                    {it.detailed_description || '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell>{it.um || '—'}</TableCell>
                                <TableCell align="right">{it.quantity || '—'}</TableCell>
                                <TableCell align="right">{it.unit_price || '—'}</TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setViewDialog({ open: false, po: null, items: [], loading: false });
              setItemSearchTerm('');
            }}
            variant="outlined"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Details Dialog */}
      <Dialog open={isEditDialogOpen} onClose={closeDetailsDialogs} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 18 }}>Edit PO</Typography>
          <Typography sx={{ color: '#667085', fontSize: 13 }}>
            You can edit Vendor / Amount / Dates (PO number is locked).
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1.5 }}>
            <TextField label="PO" value={editForm.po} disabled fullWidth />
            <TextField
              label="Vendor"
              value={editForm.vendor}
              onChange={(e) => setEditForm((p) => ({ ...p, vendor: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Total Amount *"
              value={editForm.total_amount}
              onChange={(e) => setEditForm((p) => ({ ...p, total_amount: e.target.value }))}
              fullWidth
            />
            <TextField
              label="PO Date"
              type="date"
              value={editForm.po_date || ''}
              onChange={(e) => setEditForm((p) => ({ ...p, po_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="PO Delivery Date"
              type="date"
              value={editForm.po_delivery_date || ''}
              onChange={(e) => setEditForm((p) => ({ ...p, po_delivery_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDetailsDialogs} variant="outlined" disabled={isSavingEdit}>
            Cancel
          </Button>
          <Button onClick={handleSaveEdit} variant="contained" disabled={isSavingEdit}>
            {isSavingEdit ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      <div className="purchase-order-table-container">
        <div className="purchase-order-table-wrapper">
          <MaterialReactTable table={table} />
        </div>
      </div>
    </div>
  );
});

SystemPurchaseOrderTable.displayName = 'SystemPurchaseOrderTable';

SystemPurchaseOrderTable.propTypes = {
  onEdit: PropTypes.func,
  onDeleteSuccess: PropTypes.func,
  onUploadRingsDetail: PropTypes.func,
};
